/**
 * InspectionStepReview - Step 3: Scoring, review, print
 */
import React, { useState, useMemo } from 'react';
import { Printer, ChevronDown, ChevronRight, AlertTriangle, CheckCircle } from 'lucide-react';
import {
  RESOURCE_COLUMNS,
  ALL_GROUPS,
  GROUP_LABELS,
  RANGE_GLYPHS,
  RANGE_COLORS,
  RANGE_LABELS,
  VEHICLE_MAX_SCORE,
  computeVehicleScore,
  computeFleetScore,
  scoreThreshold,
  actionForReading,
  verdictInfo,
  columnApplies,
} from '../../../../config/inspection.js';
import { formatNumber as fmtNumber } from '../../../../i18n/format.js';

// SVG ring gauge component — adapts font size to ring size
function ScoreRing({ score, size = 72, strokeWidth = 6 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(score / VEHICLE_MAX_SCORE, 1);
  const dashOffset = circumference * (1 - pct);
  const threshold = scoreThreshold(score);

  // Adaptive font sizes: large ring (72+) vs small inline ring (36-48)
  const isSmall = size <= 48;
  const scoreFontSize = isSmall ? Math.max(10, size * 0.28) : 18;
  const maxFontSize = isSmall ? Math.max(7, size * 0.17) : 9;

  return (
    <div className="relative inline-flex items-center justify-center flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
        <circle
          cx={size/2} cy={size/2} r={radius}
          fill="none"
          stroke={threshold.stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          className="transition-all duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className={`font-bold ${threshold.color}`} style={{ fontSize: scoreFontSize }}>{score}</span>
        {!isSmall && <span className="text-gray-400" style={{ fontSize: maxFontSize }}>/{VEHICLE_MAX_SCORE}</span>}
      </div>
    </div>
  );
}

export default function InspectionStepReview({
  vehicles,
  vehiclesMap,
  readings,
  odometers,
  vehicleNotes,
  onNoteChange,
  inspectionDate,
  inspectorName,
}) {
  // Compute scores
  const vehicleScores = useMemo(() => {
    return vehicles.map(v => {
      const result = computeVehicleScore(v.id, readings, v);
      return { vehicle: v, ...result };
    }).sort((a, b) => a.score - b.score); // worst first
  }, [vehicles, readings]);

  const fleetScore = computeFleetScore(
    vehicles.map(v => v.id),
    readings,
    vehiclesMap
  );

  // Count readings
  const totalReadings = vehicles.reduce((sum, v) => {
    const vr = readings[v.id] || {};
    return sum + Object.values(vr).filter(x => x !== null && x !== undefined).length;
  }, 0);

  // Count alerts
  const criticalCount = vehicles.reduce((sum, v) => {
    const vr = readings[v.id] || {};
    return sum + Object.values(vr).filter(x => x === 0).length;
  }, 0);
  const warningCount = vehicles.reduce((sum, v) => {
    const vr = readings[v.id] || {};
    return sum + Object.values(vr).filter(x => x === 1).length;
  }, 0);

  // Auto-open vehicles with problems
  const [openVehicles, setOpenVehicles] = useState(() => {
    const open = {};
    vehicles.forEach(v => {
      const vr = readings[v.id] || {};
      const hasProblems = Object.values(vr).some(x => x !== null && x < 2);
      if (hasProblems) open[v.id] = true;
    });
    return open;
  });

  const [openGroups, setOpenGroups] = useState({});

  const toggleVehicle = (id) => setOpenVehicles(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleGroup = (key) => setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));

  // Print handler — matches mockup: 1 vehicle/page, group verdict circles, signature spot
  const handlePrint = () => {
    // Helper: SVG circle for print (B&W safe — uses stroke only)
    const verdictCircle = (glyph, isOk) => {
      const stroke = isOk ? '#374151' : '#374151';
      const fill = isOk ? 'none' : 'none';
      return `<svg width="22" height="22" viewBox="0 0 22 22" style="vertical-align:middle">
        <circle cx="11" cy="11" r="9" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>
        <text x="11" y="15" text-anchor="middle" font-size="12" font-weight="bold" fill="${stroke}">${glyph}</text>
      </svg>`;
    };

    const glyphForGroup = (g, vr, v) => {
      const cols = RESOURCE_COLUMNS.filter(c => c.group === g && columnApplies(c, v));
      const filled = cols.filter(c => vr[c.key] !== null && vr[c.key] !== undefined);
      if (filled.length === 0) return { glyph: '—', isOk: true };
      const worst = Math.min(...filled.map(c => vr[c.key]));
      return { glyph: worst >= 2 ? '\u2713' : '\u2717', isOk: worst >= 2 };
    };

    const rangeLabel = (idx) => {
      const ranges = ['0-25%', '25-50%', '50-75%', '75-100%'];
      return `${RANGE_GLYPHS[idx]} ${ranges[idx]}`;
    };

    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>PV Inspection ${inspectionDate}</title>
    <style>
      @page { margin: 15mm 12mm; }
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 11px; margin: 0; color: #1f2937; }
      .page-header { text-align: center; margin-bottom: 8px; }
      .page-header h1 { font-size: 18px; margin: 0 0 4px 0; }
      .page-header .meta { color: #4b5563; font-size: 12px; }
      .page-header .meta strong { color: #1f2937; }
      .vehicle-page { page-break-after: always; }
      .vehicle-page:last-child { page-break-after: auto; }
      .vehicle-title { display: flex; align-items: center; gap: 10px; font-size: 15px; font-weight: bold; margin: 16px 0 10px 0; }
      .vehicle-title svg { flex-shrink: 0; }
      .group-badges { display: flex; gap: 20px; margin-bottom: 10px; font-size: 13px; font-weight: 600; }
      .group-badges span { display: inline-flex; align-items: center; gap: 4px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
      th { background: #f9fafb; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; }
      td, th { padding: 5px 10px; border: 1px solid #d1d5db; text-align: left; }
      td { font-size: 11px; }
      .notes { margin-top: 6px; font-style: italic; color: #6b7280; font-size: 10px; }
      .signature-block { margin-top: 32px; display: flex; gap: 60px; font-size: 12px; }
      .signature-block .sig-line { display: inline-block; width: 200px; border-bottom: 1px solid #374151; margin-left: 8px; }
    </style></head><body>`);

    vehicleScores.forEach(({ vehicle: v, score }) => {
      const vr = readings[v.id] || {};
      const titleGlyph = score >= 90 ? '\u2713' : '\u2717';

      w.document.write(`<div class="vehicle-page">
        <div class="page-header">
          <div style="font-size:10px;color:#9ca3af;margin-bottom:2px;">Inspection Groupee — FleetManager Max</div>
          <h1>Proces-verbal d'inspection — FleetManager</h1>
          <div class="meta">Date: ${inspectionDate} | Inspecteur: ${inspectorName || 'N/A'} | Vehicules: ${vehicles.length} | <strong>Score global: ${fleetScore}/${VEHICLE_MAX_SCORE}</strong></div>
        </div>

        <div class="vehicle-title">
          ${verdictCircle(titleGlyph, score >= 90)}
          ${v.license_plate} — ${v.make || ''} ${v.model || ''} — ${odometers[v.id] ? fmtNumber(odometers[v.id]) + ' km' : ''} — <strong>${score}/${VEHICLE_MAX_SCORE}</strong>
        </div>

        <div class="group-badges">
          ${ALL_GROUPS.map(g => {
            const { glyph, isOk } = glyphForGroup(g, vr, v);
            return `<span>${GROUP_LABELS[g]} ${verdictCircle(glyph, isOk)}</span>`;
          }).join('')}
        </div>

        <table>
          <thead><tr><th style="width:40%">Element</th><th style="width:30%">Etat</th><th style="width:30%">Action</th></tr></thead>
          <tbody>
          ${RESOURCE_COLUMNS.filter(c => columnApplies(c, v)).map(c => {
            const val = vr[c.key];
            if (val === null || val === undefined) return '';
            const action = actionForReading(val);
            return `<tr><td><strong>${c.label}</strong></td><td>${rangeLabel(val)}</td><td>${action.severity !== 'ok' ? action.action : ''}</td></tr>`;
          }).join('')}
          </tbody>
        </table>

        ${vehicleNotes[v.id] ? `<div class="notes">Notes: ${vehicleNotes[v.id]}</div>` : ''}

        <div class="signature-block">
          <div>Signature inspecteur: <span class="sig-line"></span></div>
          <div>Date: <span class="sig-line"></span></div>
        </div>
      </div>`);
    });

    w.document.write(`</body></html>`);
    w.document.close();
    w.print();
  };

  const fleetThreshold = scoreThreshold(fleetScore);

  return (
    <div className="space-y-6" id="bulk-inspection-pv">
      {/* Stats bar */}
      <div className="flex items-center gap-6 bg-gray-50 p-4 rounded-lg">
        <div className="text-sm text-gray-600">
          <span className="font-medium">Date:</span> {inspectionDate}
        </div>
        <div className="text-sm text-gray-600">
          <span className="font-medium">Inspecteur:</span> {inspectorName || 'N/A'}
        </div>
        <div className="text-sm text-gray-600">
          <span className="font-medium">Vehicules:</span> {vehicles.length}
        </div>
        <div className="text-sm text-gray-600">
          <span className="font-medium">Mesures:</span> {totalReadings}
        </div>
        <div className="ml-auto">
          <button
            onClick={handlePrint}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Printer className="w-4 h-4" />
            Imprimer PV
          </button>
        </div>
      </div>

      {/* Fleet score + alerts */}
      <div className="flex items-center gap-8">
        <div className="flex items-center gap-4">
          <ScoreRing score={fleetScore} size={80} strokeWidth={7} />
          <div>
            <div className={`text-lg font-bold ${fleetThreshold.color}`}>
              Score flotte moyen
            </div>
            <div className="text-sm text-gray-500">
              {verdictInfo(fleetScore).label}
            </div>
          </div>
        </div>
        <div className="h-12 w-px bg-gray-200" />
        {criticalCount > 0 && (
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">{criticalCount} critique{criticalCount > 1 ? 's' : ''}</span>
          </div>
        )}
        {warningCount > 0 && (
          <div className="flex items-center gap-2 text-orange-600">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">{warningCount} attention</span>
          </div>
        )}
        {criticalCount === 0 && warningCount === 0 && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Aucune alerte</span>
          </div>
        )}
      </div>

      {/* Incomplete warning */}
      {vehicleScores.some(vs => vs.measured < vs.total) && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Certains elements n'ont pas ete inspectes. Les elements non remplis sont comptes comme critiques dans le score.
        </div>
      )}

      {/* Per-vehicle accordion */}
      <div className="space-y-2">
        {vehicleScores.map(({ vehicle: v, score, measured, total }) => {
          const isOpen = openVehicles[v.id];
          const verdict = verdictInfo(score);
          const vr = readings[v.id] || {};

          return (
            <div key={v.id} className={`border rounded-lg overflow-hidden ${verdict.border}`}>
              {/* L1: Vehicle row */}
              <div
                onClick={() => toggleVehicle(v.id)}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 ${verdict.bg}`}
              >
                {isOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                <span className="font-mono font-bold text-sm text-gray-900">{v.license_plate}</span>
                <span className="text-sm text-gray-500">{v.make} {v.model}</span>
                {odometers[v.id] && (
                  <span className="text-xs text-gray-400">{odometers[v.id].toLocaleString()} km</span>
                )}
                <div className="ml-auto flex items-center gap-3">
                  <span className="text-xs text-gray-500">{measured}/{total} inspectes</span>
                  <ScoreRing score={score} size={36} strokeWidth={3} />
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${verdict.bg} ${verdict.color}`}>
                    {verdict.label}
                  </span>
                </div>
              </div>

              {/* L2+L3: Groups + items */}
              {isOpen && (
                <div className="px-4 pb-3 bg-white">
                  {ALL_GROUPS.map(g => {
                    const cols = RESOURCE_COLUMNS.filter(c => c.group === g && columnApplies(c, v));
                    const filledCols = cols.filter(c => vr[c.key] !== null && vr[c.key] !== undefined);
                    if (cols.length === 0) return null;

                    const groupKey = `${v.id}-${g}`;
                    const isGroupOpen = openGroups[groupKey] !== false; // default open
                    const worstInGroup = filledCols.reduce((w, c) => {
                      const val = vr[c.key];
                      return (w === null || val < w) ? val : w;
                    }, null);

                    return (
                      <div key={g} className="mt-2">
                        <div
                          onClick={() => toggleGroup(groupKey)}
                          className="flex items-center gap-2 cursor-pointer py-1"
                        >
                          {isGroupOpen ? <ChevronDown className="w-3 h-3 text-gray-400" /> : <ChevronRight className="w-3 h-3 text-gray-400" />}
                          <span className="text-xs font-semibold text-gray-600">{GROUP_LABELS[g]}</span>
                          {worstInGroup !== null && (
                            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] text-white font-bold ${RANGE_COLORS.bg[worstInGroup]}`}>
                              {RANGE_GLYPHS[worstInGroup]}
                            </span>
                          )}
                          <span className="text-xs text-gray-400">{filledCols.length}/{cols.length}</span>
                        </div>
                        {isGroupOpen && filledCols.length > 0 && (
                          <table className="w-full text-xs mt-1">
                            <thead>
                              <tr className="text-gray-500">
                                <th className="text-left py-1 px-2 font-medium">Element</th>
                                <th className="text-center py-1 px-2 font-medium">Etat</th>
                                <th className="text-left py-1 px-2 font-medium">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filledCols.map(c => {
                                const idx = vr[c.key];
                                const action = actionForReading(idx);
                                return (
                                  <tr key={c.key} className="border-t border-gray-100">
                                    <td className="py-1 px-2">{c.label}</td>
                                    <td className="py-1 px-2 text-center">
                                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${RANGE_COLORS.light[idx]} ${RANGE_COLORS.text[idx]}`}>
                                        {RANGE_GLYPHS[idx]} {RANGE_LABELS[idx]}
                                      </span>
                                    </td>
                                    <td className={`py-1 px-2 ${action.color}`}>{action.action}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                        {isGroupOpen && filledCols.length === 0 && (
                          <p className="text-xs text-gray-400 ml-5 py-1">Non inspecte</p>
                        )}
                      </div>
                    );
                  })}

                  {/* Notes */}
                  <div className="mt-3 pt-2 border-t border-gray-100">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
                    <textarea
                      value={vehicleNotes[v.id] || ''}
                      onChange={(e) => onNoteChange(v.id, e.target.value)}
                      placeholder="Observations pour ce véhicule..."
                      rows={2}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
